import { useContext, useMemo, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { CorrectedTotalDTO, DATE_FORMATS, Roles } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import Container from '../components/Layout/Container';
import GridContainer from '../components/Layout/GridContainer';
import {
    useCookieLocation,
    useCustomStyles,
    useDispatchErrorNotification,
    useLocationsByLoggedInUserRole,
} from '../hooks';
import UserContext from '../store/User/UserContext';
import {
    useBiddingCalculated,
    useCalculateBidding,
    useCalculateCorrectedResults,
    useLastBiddingCalculationDate,
    useUpdateTotals,
} from '../hooks/api/biddingCalculated';

import { formatLastUploadDate, getAllDatesInAMonth } from '../utils/dateUtils';
import { createTableRows } from '../utils/agGridUtils';
import Tabs from '../components/UI/Tabs';
import ReleaseBiddingTotalsDialog from '../components/BiddingCalculated/ReleaseBiddingTotalsDialog';
import { createMapWithTableRows, filterChangedRows } from '../components/BiddingCalculated/helpers/utils';
import {
    CorrectedTotalsReleaseState,
    deriveCorrectedTotalsReleaseState,
} from '../components/BiddingCalculated/helpers/correctedTotalsReleaseState';
import BiddingCalculatedFilters from '../components/BiddingCalculated/BiddingCalculatedFilters';
import LastTotalsReleaseDate from '../components/BiddingCalculated/LastTotalsReleaseDate';
import LastUpload from '../components/General/LastUpload';
import { useCorrectedTotals } from '../hooks/api/correctedTotals';
import { useCorrectedResults } from '../hooks/api/correctedResults';
import BiddingCalculationBackgroundProcessDialog from '../components/BiddingCalculated/BiddingCalculationBackgroundProcessDialog';
import BiddingCalculationModal from '../components/BiddingCalculated/BiddingCalculationModal';
import ExportTotalsButton from '../components/BiddingCalculated/ExportTotalsButton';
import ChangeTotalsButton from '../components/BiddingCalculated/ChangeTotalsButton';
import CompleteCalculationButton from '../components/BiddingCalculated/CompleteCalculationButton';
import { useCompletedCalculations } from '../hooks/api/completedCalculations';
import { usePublicHolidays } from '../hooks/api/publicHolidays';
import { useWorkingHoursByWorkingUnitLocation } from '../hooks/api/workingHours';
import TotalsTabPanel from '../components/BiddingCalculated/tabs/TotalsTabPanel';
import ResultsTabPanel from '../components/BiddingCalculated/tabs/ResultsTabPanel';
import PercentagesTabPanel from '../components/BiddingCalculated/tabs/PercentagesTabPanel';
import TotalsCorrectionTabPanel from '../components/BiddingCalculated/tabs/TotalsCorrectionTabPanel';
import ResultsCorrectionTabPanel from '../components/BiddingCalculated/tabs/ResultsCorrectionTabPanel';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import { hasConfiguredWorkingHours } from '../utils/workingHoursUtils';
import { useLatestOfferedReportDate } from '../hooks/api/offeredReports';
import { WorkingUnitOption } from '../components/Selects/WorkingUnitSelect';
import { QueryKey } from '../enums/QueryKey';
import { queryClient } from '../services/queryClient';
import { appTimezone } from '../config';
import dayjs, { Dayjs } from '@utils/dayjs';

const Calculated = () => {
    const { t } = useTranslation();
    const classes = useCustomStyles();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const user = useContext(UserContext);
    // TEF aliases to WFM in HEMTService.
    const canWrite = Boolean(user?.isWFMUser);
    const [openedTab, setOpenedTab] = useExpireLocalStorage<number>('bidding-calculated-tab', 0);
    const [showChangeTotalsButton, setShowChangeTotalsButton] = useState(false);
    const [isSubsequentRelease, setIsSubsequentRelease] = useState(false);

    const [showReleaseTotalsDialog, setShowReleaseTotalsDialog] = useState(false);
    const [showBiddingCalculationModal, setShowBiddingCalculationModal] = useState(false);
    const [showBiddingCalculationBackgroundProcessDialog, setShowBiddingCalculationBackgroundProcessDialog] =
        useState(false);

    const [biddingCalculationInProgress, setBiddingCalculationInProgress] = useState(false);
    const [loadingCalculateCorrectedResults, setLoadingCalculateCorrectedResults] = useState(false);
    const [showReleasedTotals, setShowReleasedTotals] = useState(true);
    const [hideNonWorkingTime, setHideNonWorkingTime] = useState(true);

    const [selectedDate, setSelectedDate] = useState(dayjs().tz(appTimezone));
    const [locationOptions] = useLocationsByLoggedInUserRole();
    const [selectedLocation, setSelectedLocation] = useCookieLocation(locationOptions);
    const [selectedWorkingUnit, setSelectedWorkingUnit] = useExpireLocalStorage<number>(
        'bidding-calculated-selected-wu',
        0
    );
    const [selectedWorkingUnitLocation, setSelectedWorkingUnitLocation] = useExpireLocalStorage<number>(
        'bidding-calculated-selected-wul',
        0
    );
    const allDatesInAMonth = useMemo(() => getAllDatesInAMonth(selectedDate), [selectedDate]);

    const { data: workingHours, isLoading: isWorkingHoursLoading } =
        useWorkingHoursByWorkingUnitLocation(selectedWorkingUnitLocation);
    const [publicHolidays] = usePublicHolidays();

    const [completedCalculations] = useCompletedCalculations({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });
    const hasCompletedCalculations = useMemo(() => completedCalculations.length > 0, [completedCalculations]);

    const { data: latestCalculationDate } = useLastBiddingCalculationDate(selectedWorkingUnitLocation);

    const lastBiddingCalculationDate = useMemo(() => {
        return formatLastUploadDate(latestCalculationDate);
    }, [latestCalculationDate]);

    const { data: latestOfferedReportDate } = useLatestOfferedReportDate({
        workingUnitLocationId: selectedWorkingUnitLocation,
        date: selectedDate,
    });

    const hasOfferedVolumeChangedAfter = useMemo(() => {
        if (!latestOfferedReportDate || !latestCalculationDate) {
            return false;
        }
        return dayjs(latestOfferedReportDate).isAfter(latestCalculationDate);
    }, [latestOfferedReportDate, latestCalculationDate]);

    const { biddingCalculated, isLoading: isBiddingCalculatedLoading } = useBiddingCalculated({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });

    const { correctedResults, isLoading: isCorrectedResultsLoading } = useCorrectedResults({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
    });

    const tableRowsBiddingCalculated = useMemo(() => {
        return biddingCalculated.length > 0
            ? createTableRows(
                  createMapWithTableRows({
                      dates: allDatesInAMonth,
                      biddingCalculated,
                      workingHours,
                      publicHolidays,
                  }),
                  'slotTime'
              )
            : [];
    }, [biddingCalculated, allDatesInAMonth, workingHours, publicHolidays]);

    const {
        data: correctedTotals,
        isLoading: isCorrectedTotalsLoading,
        refetch: refetchCorrectedTotals,
    } = useCorrectedTotals({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
        released: showReleasedTotals,
    });

    // Fetch the "other" dataset for comparison (unreleased when viewing released, vice versa)
    const { data: comparisonTotals, isLoading: isComparisonTotalsLoading } = useCorrectedTotals({
        date: selectedDate,
        workingUnitLocation: selectedWorkingUnitLocation,
        released: !showReleasedTotals,
    });

    // Release state of the selected working unit location's corrected totals for the selected month.
    // Derived from the two queries above so it stays correct regardless of the released/draft view toggle.
    const correctedTotalsReleaseState = useMemo<CorrectedTotalsReleaseState | null>(() => {
        if (
            selectedWorkingUnitLocation <= 0 ||
            isCorrectedTotalsLoading ||
            isComparisonTotalsLoading ||
            isBiddingCalculatedLoading
        ) {
            return null;
        }

        // Corrected totals only make sense for a current-or-past month that actually has bidding
        // totals to correct. Hide the alert for future months (no correction expected) and for
        // months with no bidding data at all — otherwise the red "no correction yet" is misleading.
        const isFutureMonth = dayjs(selectedDate).isAfter(dayjs().tz(appTimezone), 'month');
        if (isFutureMonth || biddingCalculated.length === 0) {
            return null;
        }

        const releasedTotals = showReleasedTotals ? correctedTotals : comparisonTotals;
        const draftTotals = showReleasedTotals ? comparisonTotals : correctedTotals;

        // The released=false query also returns legacy released rows (released=false but releaseDate
        // set), which the released=true query already counts as released (sticky semantics, CALL-742).
        // A genuine unreleased draft has no releaseDate — exclude legacy rows so they don't masquerade
        // as "newer drafts not yet released".
        const hasDrafts = draftTotals.some((total: CorrectedTotalDTO) => total.releaseDate == null);

        return deriveCorrectedTotalsReleaseState(releasedTotals.length > 0, hasDrafts);
    }, [
        selectedWorkingUnitLocation,
        isCorrectedTotalsLoading,
        isComparisonTotalsLoading,
        isBiddingCalculatedLoading,
        selectedDate,
        biddingCalculated,
        showReleasedTotals,
        correctedTotals,
        comparisonTotals,
    ]);

    const handleCellValueChange = () => {
        if (hasCompletedCalculations) {
            dispatchErrorNotification({
                message: t('bidding.planningCompleted'),
            });
            return;
        }

        setShowChangeTotalsButton(true);
    };

    const { mutate: handleBiddingCalculation } = useCalculateBidding();

    const { mutate: handleCorrectedResultsCalculation } = useCalculateCorrectedResults({
        selectedDate: selectedDate,
        selectedWorkingUnitLocation: selectedWorkingUnitLocation,
        onSettled: () => {
            setLoadingCalculateCorrectedResults(false);
        },
    });

    const handleCalculateFromModal = ({ date, workingUnits }: { date: Dayjs; workingUnits?: number[] }) => {
        setBiddingCalculationInProgress(true);
        setShowBiddingCalculationModal(false);
        setShowBiddingCalculationBackgroundProcessDialog(true);
        handleBiddingCalculation({ date: dayjs(date).tz(appTimezone).toISOString(), workingUnits: workingUnits || [] });
    };

    const { mutate: handleChangeTotals } = useUpdateTotals({ selectedDate, selectedWorkingUnitLocation });

    const changeTotals = () => {
        const changedRows = filterChangedRows(tableRowsBiddingCalculated);
        const recordsToCreate = changedRows.map((row) => ({
            ...row,
            id: null,
        }));

        handleChangeTotals(recordsToCreate);
    };

    const handleReleaseTotalsDialogClose = () => {
        setShowReleaseTotalsDialog(false);
        setIsSubsequentRelease(false);
    };

    const handleWorkingUnitLocationChange = (workingUnitLocationId: number, workingUnit?: WorkingUnitOption) => {
        setSelectedWorkingUnitLocation(workingUnitLocationId);
        setSelectedWorkingUnit(workingUnit?.id || 0);
    };

    return (
        <Container>
            <GridContainer>
                <BiddingCalculatedFilters
                    locationOptions={locationOptions}
                    selectedLocation={selectedLocation}
                    onSelectedLocationChange={setSelectedLocation}
                    selectedWorkingUnitLocation={selectedWorkingUnitLocation}
                    onSelectedWorkingUnitLocationChange={handleWorkingUnitLocationChange}
                    selectedDate={selectedDate}
                    onSelectedDateChange={setSelectedDate}
                    workingUnitHasMatchingOfferedCluster={true}
                ></BiddingCalculatedFilters>
                <Grid item xs={6} sm={4} md={2} xl={1}>
                    <LastUpload showInfoIcon={false} date={lastBiddingCalculationDate} onClick={undefined} />
                </Grid>
                <Grid item xs={6} sm={4} md={2} xl={1}>
                    <LastTotalsReleaseDate
                        text={t('field.releasedOn')}
                        workingUnitLocation={selectedWorkingUnitLocation}
                    ></LastTotalsReleaseDate>
                </Grid>
                <Grid item xs={12} xl={5}>
                    {canWrite && (
                        <>
                            <Button
                                onClick={() => setShowBiddingCalculationModal(true)}
                                variant="contained"
                                disabled={biddingCalculationInProgress}
                                sx={classes.marginRight}
                            >
                                {t('action.calculate')}
                            </Button>
                            <Button
                                onClick={() => setShowReleaseTotalsDialog(true)}
                                variant="contained"
                                sx={classes.marginRight}
                            >
                                {t('bidding.release')}
                            </Button>
                            <LoadingButton
                                onClick={() => {
                                    setLoadingCalculateCorrectedResults(true);
                                    handleCorrectedResultsCalculation({
                                        dateFrom: dayjs(selectedDate).tz(appTimezone).startOf('month').toISOString(),
                                        dateTo: dayjs(selectedDate).tz(appTimezone).endOf('month').toISOString(),
                                    });
                                }}
                                variant="contained"
                                loading={loadingCalculateCorrectedResults}
                                sx={classes.marginRight}
                            >
                                <span>{t('bidding.calculateCorrection')}</span>
                            </LoadingButton>
                        </>
                    )}
                    {canWrite && showChangeTotalsButton && (
                        <ChangeTotalsButton onChangeTotals={changeTotals} sx={classes.marginRight}></ChangeTotalsButton>
                    )}
                    {canWrite && (
                        <CompleteCalculationButton
                            dateFilter={selectedDate}
                            workingUnitLocationFilter={selectedWorkingUnitLocation}
                        ></CompleteCalculationButton>
                    )}
                </Grid>
                <Grid item xs={12} flexDirection={'row'} gap={1} display="flex" alignItems="center">
                    <ExportTotalsButton date={selectedDate} exportButtonType={Roles.CallmartTEF} location={undefined} />
                    {correctedTotalsReleaseState && (
                        <Alert
                            severity={correctedTotalsReleaseState.severity}
                            variant="outlined"
                            sx={{ py: 0, width: 'fit-content' }}
                        >
                            <span>
                                {t(correctedTotalsReleaseState.messageKey, {
                                    month: selectedDate.format(DATE_FORMATS.MONTH_NAME_YEAR),
                                })}
                            </span>
                        </Alert>
                    )}
                    {hasOfferedVolumeChangedAfter && (
                        <Alert severity="error" variant="standard" sx={{ alignItems: 'center' }}>
                            <Typography variant="subtitle1">{t('bidding.offeredVolumeChanged')}</Typography>
                        </Alert>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {selectedWorkingUnitLocation > 0 &&
                        !isWorkingHoursLoading &&
                        !hasConfiguredWorkingHours(workingHours) && (
                            <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
                                <span>{t('hint.workingHoursNotConfigured')}</span>
                            </Alert>
                        )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={hideNonWorkingTime}
                                onChange={(e) => setHideNonWorkingTime(e.target.checked)}
                                disabled={!hasConfiguredWorkingHours(workingHours)}
                            />
                        }
                        label={t('action.hideNonWorkingTime')}
                    />
                </Grid>
                <Tabs
                    defaultTab={openedTab}
                    onChange={setOpenedTab}
                    tabs={[
                        {
                            label: t('bidding.percentages'),
                            tabPanel: (
                                <PercentagesTabPanel
                                    allDatesInAMonth={allDatesInAMonth}
                                    rowData={tableRowsBiddingCalculated}
                                    isLoading={isBiddingCalculatedLoading}
                                    hideNonWorkingTime={hideNonWorkingTime}
                                    workingHours={workingHours}
                                />
                            ),
                        },
                        {
                            label: t('bidding.totals'),
                            tabPanel: (
                                <TotalsTabPanel
                                    allDatesInAMonth={allDatesInAMonth}
                                    rowData={tableRowsBiddingCalculated}
                                    handleCellValueChange={handleCellValueChange}
                                    hasCompletedCalculations={hasCompletedCalculations}
                                    isLoading={isBiddingCalculatedLoading}
                                    hideNonWorkingTime={hideNonWorkingTime}
                                    workingHours={workingHours}
                                />
                            ),
                        },
                        {
                            label: t('bidding.results'),
                            tabPanel: (
                                <ResultsTabPanel
                                    selectedDate={selectedDate}
                                    selectedWorkingUnitLocation={selectedWorkingUnitLocation}
                                    allDatesInAMonth={allDatesInAMonth}
                                    hideNonWorkingTime={hideNonWorkingTime}
                                    workingHours={workingHours}
                                    publicHolidays={publicHolidays}
                                />
                            ),
                        },
                        {
                            label: t('bidding.totalsCorrection'),
                            tabPanel: (
                                <TotalsCorrectionTabPanel
                                    allDatesInAMonth={allDatesInAMonth}
                                    correctedTotals={correctedTotals}
                                    comparisonTotals={comparisonTotals}
                                    biddingCalculated={biddingCalculated}
                                    isLoading={isCorrectedTotalsLoading}
                                    workingHours={workingHours}
                                    workingUnitLocation={selectedWorkingUnitLocation}
                                    refetchCorrectedTotals={() => {
                                        refetchCorrectedTotals();
                                        // Saving corrected totals can change the unit's draft/released
                                        // through-month, so refresh the selector captions too.
                                        queryClient.invalidateQueries({
                                            queryKey: [QueryKey.WorkingUnitLocationMetadata],
                                        });
                                    }}
                                    selectedDate={selectedDate}
                                    showReleasedTotals={showReleasedTotals}
                                    onShowReleasedTotalsChange={setShowReleasedTotals}
                                    hideNonWorkingTime={hideNonWorkingTime}
                                />
                            ),
                            tabProps: {
                                // WFM users (incl. TEF via alias) get the tab always enabled;
                                // OSP/WTM get it disabled when no data
                                disabled: !user?.isWFMUser && correctedTotals.length === 0,
                            },
                        },
                        {
                            label: t('bidding.resultsCorrection'),
                            tabPanel: (
                                <ResultsCorrectionTabPanel
                                    allDatesInAMonth={allDatesInAMonth}
                                    correctedResults={correctedResults}
                                    isLoading={isCorrectedResultsLoading}
                                    hideNonWorkingTime={hideNonWorkingTime}
                                    workingHours={workingHours}
                                    publicHolidays={publicHolidays}
                                />
                            ),
                        },
                    ]}
                ></Tabs>
                {showReleaseTotalsDialog && (
                    <ReleaseBiddingTotalsDialog
                        open={showReleaseTotalsDialog}
                        onClose={handleReleaseTotalsDialogClose}
                        date={selectedDate}
                        isSubsequentRelease={isSubsequentRelease}
                        onSubsequentReleaseChange={setIsSubsequentRelease}
                        workingUnitLocation={undefined}
                    />
                )}
                {showBiddingCalculationModal && (
                    <BiddingCalculationModal
                        open={showBiddingCalculationModal}
                        onClose={() => setShowBiddingCalculationModal(false)}
                        onCalculate={handleCalculateFromModal}
                        initialDate={selectedDate}
                        initialWorkingUnitId={selectedWorkingUnit}
                        isLoading={biddingCalculationInProgress}
                    />
                )}
                {showBiddingCalculationBackgroundProcessDialog && (
                    <BiddingCalculationBackgroundProcessDialog
                        open={showBiddingCalculationBackgroundProcessDialog}
                        onClose={() => setShowBiddingCalculationBackgroundProcessDialog(false)}
                        data={undefined}
                    />
                )}
            </GridContainer>
        </Container>
    );
};

export default Calculated;
